import { useNavigate } from 'react-router-dom';
import { IPaginationReturn } from './../../interfaces/IPaginationReturn';
import axios from 'axios';
import { useState, useContext } from 'react';
import UserContext from '../../context/user/UserContext';
import AppError from '../../errors/AppError';
import { useSessionStorage } from './useSessionStorage';

interface IRequestParams {
  path?: string;
  body?: any;
  headers?: any;
  sendAuthorization?: boolean;  
  responseType?: 'blob' | 'arraybuffer' | 'json' | 'text';
}

interface IRequest<T> {
  post(request: IRequestParams): Promise<T>;
  postAndGetManyPaginated(request: IRequestParams): Promise<IPaginationReturn<T[]>>;
  put(request: IRequestParams): Promise<T>;
  patch(request: IRequestParams): Promise<T>;
  getOne(request: IRequestParams): Promise<T>;
  getMany(request: IRequestParams): Promise<T[]>;
  getManyPaginated(request: IRequestParams): Promise<IPaginationReturn<T[]>>;
  remove(request: IRequestParams): Promise<void>;
}

const validateUrl = () => {
  if (!process.env.REACT_APP_BASE_API_URL) {
    throw new AppError('A url base da API não foi informada. É necessário verificar o arquivo .env');
  }
};

export const useRequest = <T>(): IRequest<T> => {
  const navigate = useNavigate();
  const [baseUrl] = useState(process.env.REACT_APP_BASE_API_URL);
  const { setState: setUserState, state: userState } = useContext(UserContext);
  const sessionStorage = useSessionStorage();

  const getIsLogin = (url: string): Boolean => {
    return url?.split('/').indexOf('login') !== -1;
  };

  const setUnauthorized = (): void => {
    if (window.location.pathname === '/login') {
      return;
    }
    sessionStorage.clear();
    navigate('/login');
    setUserState({
      expires_in: 0,
      username: '',
      access_token: '',
      refresh_token: '',
      name: '',
      email: '',
      tenantDomain: '',
      logged: false,
      typeAuth: '',
      fullUser: false,
      rhUser: false,
      managerUser: false,
      resources: [''],
      resourcesWithActions: { '': [''] },
    });
    throw new AppError('Sua sessão expirou. Faça o login novamente!');
  };

  const post = async (request: IRequestParams): Promise<T> => {
    validateUrl();
    const headers = { ...request.headers };
    if (request.sendAuthorization) {
      headers.Authorization = `Bearer ${userState.access_token}`;
    } 
    return await axios({
      url: `${baseUrl}/${request.path}`,
      method: 'POST',
      headers,
      data: { ...request.body },
      responseType: request.responseType
    })
      .then(res => {
        return res.data;
      })
      .catch(async error => {
        if (error?.response?.status === 401 && !getIsLogin(error?.request?.responseURL || '')) {
          return setUnauthorized();
        }

        let errorData = error?.response?.data;
        if (errorData instanceof Blob) {
          try {
            const text = await errorData.text();
            error.response.data = JSON.parse(text);
          } catch {
            error.response.data = {};
          }
        }

        throw new AppError(
          error?.response?.data?.validation?.params?.message ||
            error?.response?.data?.validation?.body?.message ||
            error?.response?.data?.validation?.query?.message ||
            error?.response?.data?.message ||
            'Ocorreu um erro ao executar o procedimento',
          error?.response?.status || 400,
        );

      });
  };

  const put = async (request: IRequestParams): Promise<any> => {
    validateUrl();
    const headers = { ...request.headers };
    if (request.sendAuthorization) {
      headers.Authorization = `Bearer ${userState.access_token}`;
    }
    return await axios({
      url: `${baseUrl}/${request.path}`,
      method: 'PUT',
      headers,
      data: { ...request.body },
    })
      .then(res => {
        return res.data;
      })
      .catch(error => {
        if (error?.response?.status === 401 && !getIsLogin(error?.request?.responseURL || '')) {
          return setUnauthorized();
        }
        throw new AppError(
          error?.response?.data?.validation?.params?.message ||
            error?.response?.data?.validation?.body?.message ||
            error?.response?.data?.validation?.query?.message ||
            error?.response?.data?.message ||
            'Ocorreu um erro ao executar o procedimento',
          error?.response?.status || 400,
        );
      });
  };

  const patch = async (request: IRequestParams): Promise<any> => {
    validateUrl();
    const headers = { ...request.headers };
    if (request.sendAuthorization) {
      headers.Authorization = `Bearer ${userState.access_token}`;
    }
    return await axios({
      url: `${baseUrl}/${request.path}`,
      method: 'PATCH',
      headers,
      data: request.body,
    })
      .then(res => {
        return res.data;
      })
      .catch(error => {
        if (error?.response?.status === 401 && !getIsLogin(error?.request?.responseURL || '')) {
          return setUnauthorized();
        }
        throw new AppError(
          error?.response?.data?.validation?.params?.message ||
            error?.response?.data?.validation?.body?.message ||
            error?.response?.data?.validation?.query?.message ||
            error?.response?.data?.message ||
            'Ocorreu um erro ao executar o procedimento',
          error?.response?.status || 400,
        );
      });
  };

  const remove = async (request: IRequestParams): Promise<void> => {
    validateUrl();
    const headers = { ...request.headers };
    if (request.sendAuthorization) {
      headers.Authorization = `Bearer ${userState.access_token}`;
    }
    return await axios({
      url: `${baseUrl}/${request.path}`,
      method: 'DELETE',
      headers,
      data: { ...request.body },
    })
      .then(res => {
        return res.data;
      })
      .catch(error => {
        if (error?.response?.status === 401 && !getIsLogin(error?.request?.responseURL || '')) {
          return setUnauthorized();
        }

        throw new AppError(
          error?.response?.data?.validation?.params?.message ||
            error?.response?.data?.validation?.body?.message ||
            error?.response?.data?.validation?.query?.message ||
            error?.response?.data?.message ||
            'Ocorreu um erro ao executar o procedimento',
          error?.response?.status || 400,
        );
      });
  };

  const getOne = async (request: IRequestParams): Promise<T> => {
    validateUrl();

    const headers = { ...request.headers };
    if (request.sendAuthorization) {
      headers.Authorization = `Bearer ${userState.access_token}`;
    }
    return await axios({
      url: `${baseUrl}/${request.path}`,
      method: 'GET',
      headers,
    })
      .then(res => {
        return res.data;
      })
      .catch(error => {
        if (error?.response?.status === 401 && !getIsLogin(error?.request?.responseURL || '')) {
          return setUnauthorized();
        }
        throw new AppError(
          error?.response?.data?.validation?.params?.message ||
            error?.response?.data?.validation?.body?.message ||
            error?.response?.data?.validation?.query?.message ||
            error?.response?.data?.message ||
            'Ocorreu um erro ao executar o procedimento',
          error?.response?.status || 400,
        );
      });
  };

  const getMany = async (request: IRequestParams): Promise<T[]> => {
    validateUrl();
    const headers = { ...request.headers };
    if (request.sendAuthorization) {
      headers.Authorization = `Bearer ${userState.access_token}`;
    }
    return await axios({
      url: `${baseUrl}/${request.path}`,
      method: 'GET',
      headers,
    })
      .then(res => {
        return res.data;
      })
      .catch(error => {
        if (error?.response?.status === 401 && !getIsLogin(error?.request?.responseURL || '')) {
          return setUnauthorized();
        }
        throw new AppError(
          error?.response?.data?.validation?.params?.message ||
            error?.response?.data?.validation?.body?.message ||
            error?.response?.data?.validation?.query?.message ||
            error?.response?.data?.message ||
            'Ocorreu um erro ao executar o procedimento',
          error?.response?.status || 400,
        );
      });
  };

  const getManyPaginated = async (request: IRequestParams): Promise<IPaginationReturn<T[]>> => {
    validateUrl();
    const headers = { ...request.headers };
    if (request.sendAuthorization) {
      headers.Authorization = `Bearer ${userState.access_token}`;
    }
    return await axios({
      url: `${baseUrl}/${request.path}`,
      method: 'GET',
      headers,
    })
      .then(res => {
        return res.data;
      })
      .catch(error => {
        if (error?.response?.status === 401 && !getIsLogin(error?.request?.responseURL || '')) {
          return setUnauthorized();
        }
        throw new AppError(
          error?.response?.data?.validation?.params?.message ||
            error?.response?.data?.validation?.body?.message ||
            error?.response?.data?.validation?.query?.message ||
            error?.response?.data?.message ||
            'Ocorreu um erro ao executar o procedimento',
          error?.response?.status || 400,
        );
      });
  };

  const postAndGetManyPaginated = async (request: IRequestParams): Promise<IPaginationReturn<T[]>> => {
    validateUrl();
    const headers = { ...request.headers };
    if (request.sendAuthorization) {
      headers.Authorization = `Bearer ${userState.access_token}`;
    }
    return await axios({
      url: `${baseUrl}/${request.path}`,
      method: 'POST',
      headers,
      data: { ...request.body },
    })
      .then(res => {
        return res.data;
      })
      .catch(error => {
        if (error?.response?.status === 401 && !getIsLogin(error?.request?.responseURL || '')) {
          return setUnauthorized();
        }
        throw new AppError(
          error?.response?.data?.validation?.params?.message ||
            error?.response?.data?.validation?.body?.message ||
            error?.response?.data?.validation?.query?.message ||
            error?.response?.data?.message ||
            'Ocorreu um erro ao executar o procedimento',
          error?.response?.status || 400,
        );
      });
  };

  return { post, getOne, getMany, put, remove, getManyPaginated, patch, postAndGetManyPaginated };
};
